<template>
    <div class="gs-lookfeel-editor">
        <div class="flex flex-col w-full bg-white rounded gs-lookfeel-shell">
            <div class="flex flex-row gs-lookfeel-layout">
                <div class="flex flex-col w-1/2 gs-lookfeel-controls">
                    <div class="relative w-full flex flex-col justify-start">
                        <gd-tabs :tabs="getLookAndFeelTabs" :current-tab="currentLookAndFeelTab"
                            :disable-not-completed="false" :wrapper-class="'px-0 gs-default-tabs'" :tab-class="mainTabClass"
                            :tab-active-class="'border-primary border-b-2 text-primary gs-default-tabs-item-active'"
                            :line-class="'gs-default-tabs-active-line'" @onClick="handleLookFeelTabChange" />
                    </div>

                    <div v-if="currentLookAndFeelTab == 'template'" class="mt-6 gs-lookfeel-panel">
                        <div class="gs-lookfeel-template-card is-selected">
                            <div class="gs-lookfeel-template-card__swatches" aria-hidden="true">
                                <span :style="{ backgroundColor: previewPrimaryColor }"></span>
                                <span :style="{ backgroundColor: previewSecondaryColor }"></span>
                            </div>
                            <div class="gs-lookfeel-template-card__copy">
                                <strong>Default checkout</strong>
                                <span>One checkout layout with customizable header and button colors.</span>
                            </div>
                            <gs-badge variant="success">Active</gs-badge>
                        </div>

                        <div class="gs-lookfeel-color-grid">
                            <label class="gs-color-field">
                                <span>{{ $t('frontend_primary_color') }}</span>
                                <small>Header and checkout button background</small>
                                <div class="gs-color-control">
                                    <input
                                        type="color"
                                        :value="previewPrimaryColor"
                                        data-cy="localModel.primary_color"
                                        @input="setCheckoutColor('primary_color', $event.target.value)"
                                    />
                                    <input
                                        class="gs-color-control__hex"
                                        :value="previewPrimaryColor"
                                        spellcheck="false"
                                        maxlength="7"
                                        @input="setCheckoutColorFromHex('primary_color', $event.target.value)"
                                        @blur="commitCheckoutColor('primary_color', $event.target.value)"
                                    />
                                </div>
                            </label>

                            <label class="gs-color-field">
                                <span>{{ $t('frontend_secondary_color') }}</span>
                                <small>Header and checkout button text</small>
                                <div class="gs-color-control">
                                    <input
                                        type="color"
                                        :value="previewSecondaryColor"
                                        data-cy="localModel.secondary_color"
                                        @input="setCheckoutColor('secondary_color', $event.target.value)"
                                    />
                                    <input
                                        class="gs-color-control__hex"
                                        :value="previewSecondaryColor"
                                        spellcheck="false"
                                        maxlength="7"
                                        @input="setCheckoutColorFromHex('secondary_color', $event.target.value)"
                                        @blur="commitCheckoutColor('secondary_color', $event.target.value)"
                                    />
                                </div>
                            </label>
                        </div>
                    </div>

                    <div v-if="currentLookAndFeelTab == 'product_image'" class="mt-6 gs-lookfeel-panel">
                        <div class="flex flex-row">
                            <div class="flex flex-col w-full mr-20">
                                <div class="flex flex-row justify-start pr-2 mb-4">
                                    <gd-toggle class="mr-2" :aria-label="$t('frontend_product_disable_branding_bar')" v-model="localModel.disable_branding_bar"
                                        :value="localModel.disable_branding_bar" />
                                    <label class="mr-4">{{
                                        $t('frontend_product_disable_branding_bar')
                                    }}</label>
                                </div>

                                <div class="flex flex-col mb-1" v-if="!localModel.disable_branding_bar">
                                    <p class="font-semibold mb-1">
                                        {{ $t('frontend_product_step5_upload') }}
                                    </p>
                                    <div class="w-1/2">
                                        <image-picker v-model="localModel.checkout_page_options.product_image"
                                            :primary="true"></image-picker>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-if="currentLookAndFeelTab == 'product_banner'" class="mt-6 gs-lookfeel-panel">
                        <div class="flex flex-row">
                            <div class="flex flex-col w-full mr-20">
                                <div class="flex flex-col mb-1">
                                    <p class="font-semibold mb-1">
                                        {{ $t('frontend_product_step5_upload_banner') }}
                                    </p>
                                    <div class="w-1/2">
                                        <image-picker v-model="localModel.checkout_page_options.product_banner"
                                            :primary="true"></image-picker>
                                    </div>
                                    <span class="text-sm text-grey-600">Tamanho Recomendado: 1440px x 400px</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-if="currentLookAndFeelTab == 'customization'" class="mt-6 gs-lookfeel-panel gs-lookfeel-settings">
                        <div class="flex flex-col">
                            <div class="flex flex-row w-full justify-start mb-4">
                                <div class="w-1/3">
                                    <label class="font-semibold text-sm text-grey-700 mb-2">{{ $t('frontend_font_family') }}
                                    </label>
                                </div>
                                <div class="w-1/3">
                                    <gd-dropdown
                                        v-model="localModel.product_customization_details.customization_details.font_family"
                                        size="full"
                                        :options="fontFamiliesOptions" 
                                    />
                                </div>
                            </div>
                        </div>
                        <div class="flex flex-col">
                            <div class="flex flex-row w-full justify-start mb-4">
                                <div class="w-1/3">
                                    <label class="font-semibold text-sm text-grey-700 mb-2">{{
                                        $t('frontend_heading_color') }}
                                    </label>
                                </div>
                                <div class="w-1/3">
                                    <div class="gs-color-control">
                                        <input
                                            type="color"
                                            :value="normalizeColor(localModel.product_customization_details.customization_details.heading_color, '#280b14')"
                                            data-cy="localModel.product_customization_details.customization_details.heading_color"
                                            @input="setCustomizationColor('heading_color', $event.target.value)"
                                        />
                                        <text-input
                                            :value="normalizeColor(localModel.product_customization_details.customization_details.heading_color, '#280b14')"
                                            @input="setCustomizationColor('heading_color', $event)"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="flex flex-col">
                            <div class="flex flex-row w-full justify-start mb-4">
                                <div class="w-1/3">
                                    <label class="font-semibold text-sm text-grey-700 mb-2">{{ $t('frontend_heading_size')
                                    }}</label>
                                </div>
                                <div class="w-1/3">
                                    <gd-dropdown
                                        v-model="localModel.product_customization_details.customization_details.heading_size"
                                        size="full"
                                        :options="sizesOptions"
                                        @change="emitChangedHeadingSize(localModel.product_customization_details.customization_details.heading_size)"
                                    />
                                </div>
                            </div>
                        </div>
                        <div class="flex flex-col">
                            <div class="flex flex-row w-full justify-start mb-4">
                                <div class="w-1/3">
                                    <label class="font-semibold text-sm text-grey-700 mb-2">{{
                                        $t('frontend_font_color') }}</label>
                                </div>
                                <div class="w-1/3">
                                    <div class="gs-color-control">
                                        <input
                                            type="color"
                                            :value="normalizeColor(localModel.product_customization_details.customization_details.lable_color, '#545671')"
                                            data-cy="localModel.product_customization_details.customization_details.lable_color"
                                            @input="setCustomizationColor('lable_color', $event.target.value)"
                                        />
                                        <text-input
                                            :value="normalizeColor(localModel.product_customization_details.customization_details.lable_color, '#545671')"
                                            @input="setCustomizationColor('lable_color', $event)"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="flex flex-col">
                            <div class="flex flex-row w-full justify-start mb-4">
                                <div class="w-1/3">
                                    <label class="font-semibold text-sm text-grey-700 mb-2">{{
                                        $t('frontend_form_field_size')
                                    }}</label>
                                </div>
                                <div class="w-1/3">
                                    <gd-dropdown
                                        v-model="localModel.product_customization_details.customization_details.input_size"
                                        size="full"
                                        :options="sizesOptions"
                                        @change="emitChangedInputSize(localModel.product_customization_details.customization_details.input_size)"
                                    />
                                </div>
                            </div>
                        </div>
                        <div class="flex flex-col">
                            <div class="flex flex-row w-full justify-start mb-4">
                                <div class="w-1/3">
                                    <label class="font-semibold text-sm text-grey-700 mb-2">{{ $t('frontend_button_size')
                                    }}</label>
                                </div>
                                <div class="w-1/3">
                                    <gd-dropdown
                                        v-model="localModel.product_customization_details.customization_details.button_size"
                                        size="full"
                                        :options="sizesOptions"
                                        @change="emitChangedButtonSize(localModel.product_customization_details.customization_details.button_size)"
                                    />
                                </div>
                            </div>
                        </div>
                        <div class="flex flex-col">
                            <div class="flex flex-row w-full justify-start mb-4">
                                <div class="w-1/3">
                                    <label class="font-semibold text-sm text-grey-700 mb-2">{{
                                        $t('frontend_full_width_button')
                                    }}</label>
                                </div>
                                <div class="w-1/3">
                                    <gd-toggle
                                        :aria-label="$t('frontend_full_width_button')"
                                        v-model="localModel.product_customization_details.customization_details.button_full_width" />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-if="currentLookAndFeelTab == 'button_text'" class="mt-6 gs-lookfeel-panel">
                        <div class="gs-lookfeel-accordion">
                            <article
                                v-for="field in buttonTextFields"
                                :key="field.key"
                                class="gs-lookfeel-accordion__item"
                                :class="{ 'is-open': openButtonTextKey === field.key }"
                            >
                                <button
                                    type="button"
                                    class="gs-lookfeel-accordion__header"
                                    @click="toggleButtonTextField(field.key)"
                                >
                                    <span>
                                        <strong>{{ field.label }}</strong>
                                        <em>{{ getButtonTextValue(field.key) || field.label }}</em>
                                    </span>
                                    <gs-icon :name="openButtonTextKey === field.key ? 'chevron-up' : 'chevron-down'" size="18"></gs-icon>
                                </button>
                                <div v-if="openButtonTextKey === field.key" class="gs-lookfeel-accordion__body">
                                    <p>{{ field.helper }}</p>
                                    <text-input
                                        :placeholder="field.label"
                                        v-model="localModel.product_customization_details.button_text_details[field.key]"
                                    />
                                </div>
                            </article>
                        </div>
                    </div>
                </div>
                <div class="flex flex-col w-1/2 gs-lookfeel-preview-panel">
                    <div class="flex flex-row">
                        <div class="text-center w-full">
                            <p class="font-semibold">{{ $t('frontend_preview') }}</p>
                        </div>
                    </div>
                    <div class="gs-checkout-preview" :style="previewStyle">
                        <div
                            v-if="!localModel.disable_branding_bar"
                            class="gs-checkout-preview__header"
                            :style="previewHeaderStyle"
                        >
                            <div class="gs-checkout-preview__logo">
                                <img
                                    v-if="ProductHasImage"
                                    alt=""
                                    :src="localModel.checkout_page_options.product_image"
                                />
                                <span v-else>G</span>
                            </div>
                            <div class="gs-checkout-preview__header-copy">
                                <strong :style="previewSecondaryTextStyle">{{ localModel.name }}</strong>
                                <p :style="previewSecondaryTextStyle">{{ localModel.description }}</p>
                            </div>
                        </div>

                        <div class="gs-checkout-preview__body">
                            <h4>{{ $t('frontend_account_information') }}</h4>
                            <div class="gs-checkout-preview__field-grid">
                                <label>
                                    <span>{{ $t('frontend_first_name') }}</span>
                                    <em>{{ $t('frontend_first_name') }}</em>
                                </label>
                                <label>
                                    <span>{{ $t('frontend_last_name') }}</span>
                                    <em>{{ $t('frontend_last_name') }}</em>
                                </label>
                            </div>
                            <label class="gs-checkout-preview__field">
                                <span>Email</span>
                                <em>Email</em>
                            </label>
                            <button
                                type="button"
                                class="gs-checkout-preview__button"
                                :style="previewButtonStyle"
                            >
                                {{ getButtonTextValue('complete_purchase') || $t('frontend_complete_purchase') }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

    </div>
</template>
<script>
import ImagePicker from '@groovepages/gd-ui-kit/components/ImagePickerModal';

export default {

    components: {
        ImagePicker,
    },
    props: {
        model: {
            required: true
        },
        customColor: {
            type: Object,
            required: true
        },
    },
    data() {
        return {
            localModel: this.model,
            fontFamilies: [
                {
                    id: 1,
                    value: 'Abril Fatface',
                },
                {
                    id: 2,
                    value: 'Amatic SC',
                },
                {
                    id: 3,
                    value: 'Architects Daughter',
                },
                {
                    id: 4,
                    value: 'Asap',
                },
                {
                    id: 5,
                    value: 'Balsamiq Sans',
                },
                {
                    id: 6,
                    value: 'Barlow',
                },
                {
                    id: 7,
                    value: 'Bebas Neue',
                },
                {
                    id: 8,
                    value: 'Bitter',
                },
                {
                    id: 9,
                    value: 'Cabin',
                },
                {
                    id: 10,
                    value: 'Cairo',
                },
                {
                    id: 11,
                    value: 'Cormorant Garamond',
                },
                {
                    id: 12,
                    value: 'Crimson Text',
                },
                {
                    id: 13,
                    value: 'Dancing Script',
                },
                {
                    id: 14,
                    value: 'Fira Sans',
                },
                {
                    id: 15,
                    value: 'Fjalla One',
                },
                {
                    id: 16,
                    value: 'Indie Flower',
                },
                {
                    id: 17,
                    value: 'Josefin Sans',
                },
                {
                    id: 18,
                    value: 'Lato',
                },
                {
                    id: 19,
                    value: 'Libre',
                },
                {
                    id: 20,
                    value: 'Libre Franklin',
                },
                {
                    id: 21,
                    value: 'Lobster',
                },
                {
                    id: 22,
                    value: 'Lora',
                },
                {
                    id: 23,
                    value: 'Martel',
                },
                {
                    id: 24,
                    value: 'Merriweather',
                },
                {
                    id: 25,
                    value: 'Montserrat',
                },
                {
                    id: 26,
                    value: 'Mukta',
                },
                {
                    id: 27,
                    value: 'Noto+Sans',
                },
                {
                    id: 28,
                    value: 'Noto+Sans+KR',
                },
                {
                    id: 29,
                    value: 'Noto Sans',
                },
                {
                    id: 30,
                    value: 'Noto Serif',
                },
                {
                    id: 31,
                    value: 'Nunito Sans',
                },
                {
                    id: 32,
                    value: 'Nunito',
                },
                {
                    id: 33,
                    value: 'Old Standard TT',
                },
                {
                    id: 34,
                    value: 'Open Sans Condensed',
                },
                {
                    id: 35,
                    value: 'Open Sans',
                },
                {
                    id: 36,
                    value: 'Oswald',
                },
                {
                    id: 37,
                    value: 'Overpass',
                },
                {
                    id: 38,
                    value: 'Oxygen',
                },
                {
                    id: 39,
                    value: 'PT Sans Narrow',
                },
                {
                    id: 40,
                    value: 'PT Sans',
                },
                {
                    id: 41,
                    value: 'PT Serif',
                },
                {
                    id: 42,
                    value: 'Pacifico',
                },
                {
                    id: 43,
                    value: 'Playfair Display',
                },
                {
                    id: 44,
                    value: 'Poppins',
                },
                {
                    id: 45,
                    value: 'Raleway',
                },
                {
                    id: 46,
                    value: 'Roboto Condensed',
                },
                {
                    id: 47,
                    value: 'Roboto Slab',
                },
                {
                    id: 48,
                    value: 'Roboto',
                },
                {
                    id: 49,
                    value: 'Rubik',
                },
                {
                    id: 50,
                    value: 'Shadows Into Light',
                },
                {
                    id: 51,
                    value: 'Signika',
                },
                {
                    id: 52,
                    value: 'Slabo 27px',
                },
                {
                    id: 53,
                    value: 'Source Code Pro',
                },
                {
                    id: 54,
                    value: 'Source Sans',
                },
                {
                    id: 55,
                    value: 'Source Serif Pro',
                },
                {
                    id: 56,
                    value: 'Tajawal',
                },
                {
                    id: 57,
                    value: 'Titillium Web',
                },
                {
                    id: 58,
                    value: 'Ubuntu',
                },
                {
                    id: 59,
                    value: 'Work Sans',
                },
            ],
            sizes: [
                {
                    id: 1,
                    title: 'Small',
                    value: 'small',
                },
                {
                    id: 2,
                    title: 'Regular',
                    value: 'regular',
                },
                {
                    id: 3,
                    title: 'Large',
                    value: 'large',
                },
            ],
            colors: {
                'primary-darkest': 'hsla(213, 90%, 10%, 1.00)',
                'primary-darker': 'hsla(213, 95%, 25%, 1.00)',
                'primary-dark': 'hsla(213, 95%, 35%, 1.00)',
                primary: 'hsla(213, 100%, 59%, 1.00)',
                'primary-light': 'hsla(213, 100%, 70%, 1.00)',
                'primary-lighter': 'hsla(213, 100%, 83%, 1.00)',
                'primary-lightest': 'hsla(213, 100%, 93%, 1.00)',

                'secondary-darkest': 'hsla(342, 55%, 10%, 1.00)',
                'secondary-darker': 'hsla(342, 65%, 25%, 1.00)',
                'secondary-dark': 'hsla(342, 65%, 38%, 1.00)',
                secondary: 'hsla(342, 75%, 57%, 1.00)',
                'secondary-light': 'hsla(342, 75%, 65%, 1.00)',
                'secondary-lighter': 'hsla(342, 75%, 80%, 1.00)',
                'secondary-lightest': 'hsla(342, 85%, 93%, 1.00)',

                'grey-darkest': 'hsla(213, 40%, 10%, 1.00)',
                'grey-darker': 'hsla(213, 30%, 18%, 1.00)',
                'grey-dark': 'hsla(202, 20%, 28%, 1)',
                grey: 'hsla(213, 15%, 54%, 1.00)',
                'grey-light': 'hsla(213, 15%, 72%, 1.00)',
                'grey-lighter': 'hsla(213, 15%, 85%, 1.00)',
                'grey-lightest': 'hsla(213, 15%, 95%, 1.00)',

                'red-darkest': 'hsla(0, 90%, 12%, 1.00)',
                'red-darker': 'hsla(0, 80%, 25%, 1.00)',
                'red-dark': 'hsla(0, 80%, 48%, 1.00)',
                red: 'hsla(0, 99%, 64%, 1.00)',
                'red-light': 'hsla(0, 99%, 71%, 1.00)',
                'red-lighter': 'hsla(0, 99%, 83%, 1.00)',
                'red-lightest': 'hsla(0, 99%, 93%, 1.00)',

                'green-darkest': 'hsla(143, 69%, 10%, 1.00)',
                'green-darker': 'hsla(143, 79%, 20%, 1.00)',
                'green-dark': 'hsla(143, 79%, 30%, 1.00)',
                green: 'hsla(143, 79%, 45%, 1.00)',
                'green-light': 'hsla(143, 75%, 55%, 1.00)',
                'green-lighter': 'hsla(143, 75%, 70%, 1.00)',
                'green-lightest': 'hsla(143, 80%, 80%, 1.00)',

                'yellow-darkest': 'hsla(20, 90%, 20%, 1.00)',
                'yellow-darker': 'hsla(30, 100%, 30%, 1.00)',
                'yellow-dark': 'hsla(40, 100%, 40%, 1.00)',
                yellow: 'hsla(46, 100%, 57%, 1.00)',
                'yellow-light': 'hsla(46, 95%, 69%, 1.00)',
                'yellow-lighter': 'hsla(46, 95%, 79%, 1.00)',
                'yellow-lightest': 'hsla(46, 90%, 92%, 1.00)',

                purple: 'hsla(227, 49%, 55%, 1.00)',
                indigo: 'hsla(225, 63%, 49%, 1.00)',
                blue: 'hsla(209, 63%, 43%, 1.00)',
            },
            currentLookAndFeelTab: 'template',
            openButtonTextKey: 'continue_to_billing',
            defaultCheckoutPrimaryColor: '#d9237f',
            defaultCheckoutSecondaryColor: '#ffffff',
            lookAndFeelTabs: [
                {
                    title: `<span class="text-sm mr-2 ml-1 text-xs font-semibold">${this.$t('frontend_template')}<span>`,
                    value: 'template',
                    show: true
                },
                {
                    title: `<span class="text-sm mr-2 ml-1 text-xs font-semibold">${this.$t('frontend_product_image')}<span>`,
                    value: 'product_image',
                    show: true
                },
                {
                    title: `<span class="text-sm mr-2 ml-1 text-xs font-semibold">${this.$t('frontend_product_banner_image')}<span>`,
                    value: 'product_banner',
                    show: false
                },
                {
                    title: `<span class="text-sm mr-2 ml-1 text-xs font-semibold">${this.$t('frontend_customization')} <span>`,
                    value: 'customization',
                    show: true
                },
                {
                    title: `<span class="text-sm mr-2 ml-1 text-xs font-semibold">${this.$t('frontend_button_text')} <span>`,
                    value: 'button_text',
                    show: true
                },
            ],
        }
    },

    computed: {
        ProductHasImage() {
            return this.localModel.checkout_page_options.hasOwnProperty('product_image') &&
                this.localModel.checkout_page_options.product_image &&
                this.localModel.checkout_page_options.product_image != ''
        },
        mainTabClass() {
            return 'pb-4 gs-default-tabs-item';
        },
        getLookAndFeelTabs() {
            return this.lookAndFeelTabs.filter(tab => tab.show)
        },
        previewStyle() {
            const customStyle = { ...this.customColor };

            customStyle['--primary-color'] = this.previewPrimaryColor;
            customStyle['--secondary-color'] = this.previewSecondaryColor;

            return customStyle;
        },
        previewPrimaryColor() {
            return this.normalizeColor(
                this.localModel.primary_color || this.customColor['--primary-color'],
                this.defaultCheckoutPrimaryColor
            );
        },
        previewSecondaryColor() {
            return this.normalizeColor(
                this.localModel.secondary_color || this.customColor['--secondary-color'],
                this.defaultCheckoutSecondaryColor
            );
        },
        previewHeaderStyle() {
            return {
                backgroundColor: this.previewPrimaryColor,
            };
        },
        previewSecondaryTextStyle() {
            return {
                color: this.previewSecondaryColor,
            };
        },
        previewButtonStyle() {
            return {
                backgroundColor: this.previewPrimaryColor,
                borderColor: this.previewPrimaryColor,
                color: this.previewSecondaryColor,
            };
        },
        fontFamiliesOptions() {
            return this.fontFamilies.map(family => {
                return {
                    value: family.value,
                    text: family.value
                }
            })
        },
        sizesOptions() {
            return this.sizes.map(size => {
                return {
                    value: size.value,
                    text: size.title
                }
            })
        },
        buttonTextFields() {
            return [
                {
                    key: 'continue_to_billing',
                    label: this.$t('frontend_continue_to_billing'),
                    helper: 'Shown when the buyer moves from account information into billing details.',
                },
                {
                    key: 'continue_to_password',
                    label: this.$t('frontend_continue_to_password'),
                    helper: 'Shown when checkout asks the buyer to create or confirm a password.',
                },
                {
                    key: 'continue_to_payment',
                    label: this.$t('frontend_continue_to_payment'),
                    helper: 'Shown before payment fields are presented.',
                },
                {
                    key: 'continue_to_order',
                    label: this.$t('frontend_continue_to_order'),
                    helper: 'Shown before the buyer reviews or confirms the order.',
                },
                {
                    key: 'complete_purchase',
                    label: this.$t('frontend_complete_purchase'),
                    helper: 'Shown on the final paid checkout button.',
                },
                {
                    key: 'get_free_access',
                    label: this.$t('frontend_get_free_access'),
                    helper: 'Shown when the selected price point is free.',
                },
            ];
        }
    },
    watch: {
        model: {
            handler(val) {
                this.localModel = val;
            },
            deep: true,
        },
        localModel: {
            handler: function (val, oldVal) {
                this.$emit('update-model', val);
            },
            deep: true
        },
    },
    methods: {
        handleLookFeelTabChange(newTab) {
            this.currentLookAndFeelTab = newTab;
        },
        toggleButtonTextField(key) {
            this.openButtonTextKey = this.openButtonTextKey === key ? null : key;
        },
        getButtonTextValue(key) {
            return this.localModel.product_customization_details.button_text_details[key];
        },
        normalizeColor(value, fallback = '#ffffff') {
            const rawValue = value && value.target ? value.target.value : value;
            if (!rawValue || rawValue === 'transparent') return fallback;

            const trimmed = String(rawValue).trim();
            if (/^#[0-9a-f]{6}$/i.test(trimmed)) return trimmed;

            if (/^#[0-9a-f]{3}$/i.test(trimmed)) {
                return `#${trimmed[1]}${trimmed[1]}${trimmed[2]}${trimmed[2]}${trimmed[3]}${trimmed[3]}`.toLowerCase();
            }

            const rgbaMatch = trimmed.match(/rgba?\(([^)]+)\)/i);
            if (rgbaMatch) {
                const values = rgbaMatch[1].split(',').map(item => parseInt(item.trim(), 10));
                if (values.length >= 3 && values.every((item, index) => index > 2 || !Number.isNaN(item))) {
                    return this.rgbToHex(values[0], values[1], values[2]);
                }
            }

            if (trimmed === 'white') return '#ffffff';
            if (trimmed === 'black') return '#000000';

            return fallback;
        },
        ensureCustomTemplateState() {
            if (!this.localModel.checkout_page_options.look_and_feel) {
                this.$set(this.localModel.checkout_page_options, 'look_and_feel', {});
            }

            if (this.localModel.checkout_page_options.look_and_feel.template_type !== 4) {
                this.$set(this.localModel.checkout_page_options.look_and_feel, 'template_type', 4);
            }

            if (!this.localModel.checkout_page_options.look_and_feel.custom_color) {
                this.$set(this.localModel.checkout_page_options.look_and_feel, 'custom_color', {});
            }
        },
        setCheckoutColor(key, value) {
            const normalized = this.normalizeColor(
                value,
                key === 'secondary_color' ? this.defaultCheckoutSecondaryColor : this.defaultCheckoutPrimaryColor
            );

            this.ensureCustomTemplateState();
            this.localModel[key] = normalized;
            this.$set(this.localModel.checkout_page_options.look_and_feel.custom_color, key, normalized);
            this.$emit('update-attributes', {
                key,
                value: normalized,
            });
        },
        setCheckoutColorFromHex(key, value) {
            const trimmed = String(value || '').trim();
            if (/^#[0-9a-f]{3}$/i.test(trimmed) || /^#[0-9a-f]{6}$/i.test(trimmed)) {
                this.setCheckoutColor(key, trimmed);
            }
        },
        commitCheckoutColor(key, value) {
            this.setCheckoutColor(key, value);
        },
        setCustomizationColor(key, value) {
            const fallback = key === 'heading_color' ? '#280b14' : '#545671';
            const normalized = this.normalizeColor(value, fallback);
            this.localModel.product_customization_details.customization_details[key] = normalized;
            if (key === 'heading_color') this.changedHeadingColor();
            if (key === 'lable_color') this.changedLabelColor();
        },
        changedLogoBG(type) {
            if (type == 1 && this.localModel.primary_color != '') {
                this.setCheckoutColor('primary_color', this.localModel.primary_color);
            }

            if (type == 2 && this.localModel.secondary_color != '') {
                this.setCheckoutColor('secondary_color', this.localModel.secondary_color);
            }
        },
        changedHeadingColor() {
            if (this.localModel.product_customization_details.customization_details.heading_color != '') {
                const colorHex = this.normalizeColor(
                    this.localModel.product_customization_details.customization_details.heading_color,
                    '#280b14'
                );
                this.localModel.product_customization_details.customization_details.heading_color = colorHex;
                this.$emit('update-attributes', {
                    key: 'heading_color',
                    value: colorHex,
                });
            }
        },
        changedLabelColor() {
            if (this.localModel.product_customization_details.customization_details.lable_color != '') {
                const colorHex = this.normalizeColor(
                    this.localModel.product_customization_details.customization_details.lable_color,
                    '#545671'
                );
                this.localModel.product_customization_details.customization_details.lable_color = colorHex;
                this.$emit('update-attributes', {
                    key: 'lable_color',
                    value: colorHex,
                });
            }
        },
        rgbToHex(r, g, b) {
            return `#${this.componentToHex(r)}${this.componentToHex(g)}${this.componentToHex(b)}`;
        },
        componentToHex(c) {
            const hex = c.toString(16);
            return hex.length == 1 ? `0${hex}` : hex;
        },
        emitChangedButtonSize(val) {
            this.$emit('update-attributes', {
                key: 'button_size',
                value: val,
            });
        },
        emitChangedHeadingSize(val) {
            this.$emit('update-attributes', {
                key: 'heading_size',
                value: val,
            });
        },
        emitChangedInputSize(val) {
            this.$emit('update-attributes', {
                key: 'input_size',
                value: val,
            });
        },
    }
}

</script>
<style>
.gs-lookfeel-editor,
.gs-lookfeel-shell,
.gs-lookfeel-layout,
.gs-lookfeel-controls {
    overflow: visible !important;
}

.gs-lookfeel-shell {
    background: transparent !important;
    border-radius: 0 !important;
}

.gs-lookfeel-layout {
    display: flex !important;
    flex-direction: row !important;
    gap: 22px;
    align-items: flex-start;
}

.gs-lookfeel-controls {
    width: 66% !important;
    flex: 1 1 0;
    min-width: 0;
}

.gs-lookfeel-preview-panel {
    width: 34% !important;
    flex: 0 0 34%;
}

.gs-lookfeel-editor .gs-default-tabs {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    padding: 0 !important;
    border: 0 !important;
}

.gs-lookfeel-editor .gs-default-tabs-item {
    min-height: 42px !important;
    padding: 0 16px !important;
    color: var(--gs-muted-foreground) !important;
    background: var(--gs-surface) !important;
    border: 1px solid var(--gs-border) !important;
    border-radius: 6px !important;
}

.gs-lookfeel-editor .gs-default-tabs-item-active {
    color: var(--gs-active) !important;
    background: var(--gs-active-soft) !important;
    border-color: var(--gs-active) !important;
    box-shadow: 0 0 0 3px var(--gs-active-shadow);
}

.gs-lookfeel-panel {
    padding: 18px;
    background: var(--gs-surface-subtle);
    border: 1px solid var(--gs-border);
    border-radius: var(--gs-radius-md);
}

.gs-lookfeel-template-card {
    display: grid;
    grid-template-columns: 56px minmax(0, 1fr) auto;
    gap: 14px;
    align-items: center;
    padding: 16px;
    background: var(--gs-surface);
    border: 1px solid var(--gs-border);
    border-radius: var(--gs-radius-md);
    box-shadow: var(--gs-shadow-sm);
}

.gs-lookfeel-template-card.is-selected {
    border-color: var(--gs-active);
    box-shadow: 0 0 0 3px var(--gs-active-shadow), var(--gs-shadow-sm);
}

.gs-lookfeel-template-card__swatches {
    position: relative;
    width: 48px;
    height: 48px;
}

.gs-lookfeel-template-card__swatches span {
    position: absolute;
    display: block;
    width: 34px;
    height: 34px;
    border: 2px solid var(--gs-surface);
    border-radius: 999px;
    box-shadow: var(--gs-shadow-sm);
}

.gs-lookfeel-template-card__swatches span:first-child {
    top: 0;
    left: 0;
}

.gs-lookfeel-template-card__swatches span:last-child {
    right: 0;
    bottom: 0;
}

.gs-lookfeel-template-card__copy strong {
    display: block;
    color: var(--gs-foreground);
    font-size: 15px;
    font-weight: 650;
    line-height: 1.3;
}

.gs-lookfeel-template-card__copy span {
    display: block;
    margin-top: 4px;
    color: var(--gs-muted-foreground);
    font-size: 13px;
    font-weight: 500;
    line-height: 1.4;
}

.gs-lookfeel-color-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px;
    margin-top: 16px;
}

.gs-color-field {
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 0;
    padding: 14px;
    background: var(--gs-surface);
    border: 1px solid var(--gs-border);
    border-radius: var(--gs-radius-md);
}

.gs-color-field span {
    color: var(--gs-foreground);
    font-size: 14px;
    font-weight: 650;
    line-height: 1.25;
}

.gs-color-field small {
    color: var(--gs-muted-foreground);
    font-size: 12px;
    font-weight: 500;
    line-height: 1.35;
}

.gs-lookfeel-editor .gd-toggle-switch.active > span,
.gs-lookfeel-editor .gd-toggle-switch.active span.bg-primary,
.gs-lookfeel-editor .gd-toggle-switch.active .bg-primary {
    background: var(--gs-primary, var(--brand-primary)) !important;
}

.gs-lookfeel-panel p,
.gs-lookfeel-panel label {
    color: var(--gs-foreground) !important;
    font-size: 14px !important;
    font-weight: 650 !important;
    line-height: 1.35;
}

.gs-lookfeel-panel > .flex,
.gs-lookfeel-panel .flex.flex-row {
    gap: 14px;
}

.gs-lookfeel-panel .mr-20,
.gs-lookfeel-panel .ml-10,
.gs-lookfeel-panel .pr-2 {
    margin-right: 0 !important;
    margin-left: 0 !important;
    padding-right: 0 !important;
}

.gs-lookfeel-panel > .flex > .flex-col.w-full,
.gs-lookfeel-settings > .flex > .flex-row > .w-1\/3,
.gs-lookfeel-settings > .flex > .flex-row > .w-2\/3,
.gs-lookfeel-settings > .flex > .flex-row > .w-4\/5 {
    width: 100% !important;
}

.gs-lookfeel-settings > .flex > .flex-row {
    display: grid !important;
    grid-template-columns: minmax(180px, 260px) minmax(240px, 1fr);
    align-items: center;
    gap: 16px;
    margin-bottom: 14px !important;
}

.gs-color-control {
    display: grid;
    grid-template-columns: 46px minmax(120px, 1fr);
    gap: 10px;
    align-items: center;
}

.gs-color-control input[type="color"] {
    width: 46px;
    height: 40px;
    padding: 4px;
    background: var(--gs-surface);
    border: 1px solid var(--gs-border);
    border-radius: var(--gs-radius-sm);
    box-shadow: var(--gs-shadow-sm);
    cursor: pointer;
}

.gs-color-control input[type="color"]::-webkit-color-swatch-wrapper {
    padding: 0;
}

.gs-color-control input[type="color"]::-webkit-color-swatch {
    border: 0;
    border-radius: var(--gs-radius-sm);
}

.gs-color-control__hex {
    min-width: 0;
    height: 40px;
    padding: 0 12px;
    color: var(--gs-foreground);
    font-family: var(--gs-font-mono);
    font-size: 13px;
    font-weight: 600;
    background: var(--gs-input);
    border: 1px solid var(--gs-border);
    border-radius: var(--gs-radius-sm);
    outline: none;
}

.gs-color-control__hex:focus {
    border-color: var(--gs-active);
    box-shadow: 0 0 0 3px var(--gs-active-shadow);
}

.gs-lookfeel-accordion {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.gs-lookfeel-accordion__item {
    overflow: hidden;
    background: var(--gs-surface);
    border: 1px solid var(--gs-border);
    border-radius: var(--gs-radius-md);
    box-shadow: var(--gs-shadow-sm);
    transition: border-color .16s ease, box-shadow .16s ease, background .16s ease;
}

.gs-lookfeel-accordion__item.is-open {
    background: var(--gs-active-soft);
    border-color: var(--gs-active);
    box-shadow: 0 0 0 3px var(--gs-active-shadow), var(--gs-shadow-sm);
}

.gs-lookfeel-accordion__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    width: 100%;
    padding: 16px 18px;
    color: var(--gs-foreground);
    text-align: left;
    background: transparent;
    border: 0;
    cursor: pointer;
}

.gs-lookfeel-accordion__header span {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
}

.gs-lookfeel-accordion__header strong {
    color: var(--gs-foreground);
    font-size: 15px;
    font-weight: 650;
    line-height: 1.3;
}

.gs-lookfeel-accordion__header em {
    overflow: hidden;
    color: var(--gs-muted-foreground);
    font-size: 13px;
    font-style: normal;
    font-weight: 500;
    line-height: 1.35;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.gs-lookfeel-accordion__body {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 0 18px 18px;
}

.gs-lookfeel-accordion__body p {
    margin: 0;
    color: var(--gs-muted-foreground) !important;
    font-size: 13px !important;
    font-weight: 500 !important;
    line-height: 1.45;
}

.gs-lookfeel-preview-panel {
    padding: 18px;
    background: var(--gs-surface);
    border: 1px solid var(--gs-border);
    border-radius: var(--gs-radius-md);
    box-shadow: var(--gs-shadow-sm);
}

.gs-lookfeel-preview-panel > .flex:first-child p {
    margin: 0 0 14px;
    color: var(--gs-foreground);
    font-size: 15px;
    font-weight: 650;
    text-align: left;
}

.gs-checkout-preview {
    overflow: hidden;
    background: var(--gs-surface);
    border: 1px solid var(--gs-border);
    border-radius: 8px;
}

.gs-checkout-preview__header {
    display: grid;
    grid-template-columns: 72px minmax(0, 1fr);
    gap: 16px;
    align-items: center;
    min-height: 96px;
    padding: 16px;
    background: var(--primary-color);
}

.gs-checkout-preview__logo {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 62px;
    height: 62px;
    overflow: hidden;
    color: var(--primary-color);
    font-size: 30px;
    font-weight: 800;
    line-height: 1;
    background: var(--secondary-color);
    border-radius: 8px;
}

.gs-checkout-preview__logo img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.gs-checkout-preview__header-copy {
    min-width: 0;
}

.gs-checkout-preview__header-copy strong,
.gs-checkout-preview__header-copy p {
    display: block;
    margin: 0;
    color: var(--secondary-color) !important;
}

#gs-product-wizard-root .gs-checkout-step .gs-checkout-preview__header-copy strong,
#gs-product-wizard-root .gs-checkout-step .gs-checkout-preview__header-copy p {
    color: var(--secondary-color) !important;
}

.gs-checkout-preview__header-copy strong {
    overflow: hidden;
    font-size: 18px;
    font-weight: 700;
    line-height: 1.25;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.gs-checkout-preview__header-copy p {
    margin-top: 8px;
    font-size: 13px;
    font-weight: 500;
    line-height: 1.35;
}

.gs-checkout-preview__body {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 18px;
}

.gs-checkout-preview__body h4 {
    margin: 0;
    color: var(--gs-muted-foreground);
    font-size: 15px;
    font-weight: 700;
    line-height: 1.3;
}

.gs-checkout-preview__field-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 14px;
}

.gs-checkout-preview__field,
.gs-checkout-preview__field-grid label {
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 0;
}

.gs-checkout-preview__field span,
.gs-checkout-preview__field-grid span {
    color: var(--gs-foreground);
    font-size: 13px;
    font-weight: 700;
    line-height: 1.3;
}

.gs-checkout-preview__field em,
.gs-checkout-preview__field-grid em {
    display: flex;
    align-items: center;
    min-height: 40px;
    padding: 0 14px;
    overflow: hidden;
    color: var(--gs-muted-foreground);
    font-size: 13px;
    font-style: normal;
    line-height: 1.2;
    white-space: nowrap;
    background: var(--gs-input);
    border: 1px solid var(--gs-border);
    border-radius: 6px;
    box-shadow: var(--gs-shadow-sm);
}

.gs-checkout-preview__button {
    display: inline-flex !important;
    align-items: center;
    justify-content: center;
    width: 100%;
    min-height: 46px;
    padding: 0 18px;
    color: var(--secondary-color) !important;
    font-size: 14px;
    font-weight: 700;
    line-height: 1.2;
    background: var(--primary-color) !important;
    border: 1px solid var(--primary-color) !important;
    border-radius: 6px;
    box-shadow: var(--gs-shadow-sm);
}

.gs-lookfeel-preview-panel .custom-font-family {
    border-radius: 8px 8px 0 0;
}

.gs-lookfeel-preview-panel .custom-template-color-primary {
    background-color: var(--primary-color) !important;
}

.gs-lookfeel-preview-panel .custom-template-color-secondary-text,
.gs-lookfeel-preview-panel .custom-template-color-secondary-text *,
.gs-lookfeel-preview-panel button.custom-template-color-secondary-text,
.gs-lookfeel-preview-panel .gd-button.custom-template-color-secondary-text {
    color: var(--secondary-color) !important;
}

.gs-lookfeel-preview-panel .custom-button {
    display: inline-flex !important;
    align-items: center;
    justify-content: center;
    width: 100%;
    min-height: var(--button-size, 2.5rem) !important;
    color: var(--secondary-color) !important;
    font-size: var(--button-text, 14px) !important;
    line-height: 1.2 !important;
    background-color: var(--primary-color) !important;
    border-color: var(--primary-color) !important;
}

.gs-lookfeel-preview-panel button.custom-button,
.gs-lookfeel-preview-panel .custom-button.bg-primary,
.gs-lookfeel-preview-panel .custom-button.hover\:bg-primary-dark:hover {
    color: var(--secondary-color) !important;
    background-color: var(--primary-color) !important;
    border-color: var(--primary-color) !important;
}

.gs-lookfeel-preview-panel .custom-button * {
    color: var(--secondary-color) !important;
}

.gs-lookfeel-preview-panel .preview {
    gap: 12px;
}

.gs-lookfeel-preview-panel .preview .w-1\/2 {
    width: 50% !important;
}

@media (max-width: 840px) {
    .gs-lookfeel-layout {
        flex-direction: column !important;
    }

    .gs-lookfeel-controls,
    .gs-lookfeel-preview-panel {
        width: 100% !important;
        flex-basis: auto;
    }

    .gs-lookfeel-settings > .flex > .flex-row {
        grid-template-columns: 1fr;
        gap: 8px;
    }

    .gs-lookfeel-template-card,
    .gs-lookfeel-color-grid {
        grid-template-columns: 1fr;
    }

    .gs-lookfeel-preview-panel .preview {
        flex-direction: column !important;
    }

    .gs-lookfeel-preview-panel .preview .w-1\/2 {
        width: 100% !important;
    }

    .gs-checkout-preview__field-grid,
    .gs-checkout-preview__header {
        grid-template-columns: 1fr;
    }
}
</style>
